import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PARTY_LABEL } from "../public/AsumsiBagiHasil";

const SCEN = ["KONSERVATIF", "MODERAT", "OPTIMISTIS"];
const PARTIES = ["PEMODAL", "PENGELOLA", "PEMILIK_LAHAN", "PT_SJI"];
const list = (s) => String(s).split(/[,;\s]+/).filter(Boolean).map(Number);
const F = ({ label, value, onChange, testid }) => (
  <div className="space-y-1"><Label className="text-xs">{label}</Label><Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>
);

const DensityEditor = ({ d, onChange, i }) => {
  const set = (k, sub) => (v) => onChange(sub ? { ...d, [k]: { ...d[k], [sub]: v } } : { ...d, [k]: v });
  return (
    <div className="rounded-lg border border-slate-200 p-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`param-density-${i}`}>
      <F label="Jumlah batang/ha" value={d.trees} onChange={set("trees")} testid={`param-trees-${i}`} />
      <F label="Modal awal (Rp)" value={d.capital} onChange={set("capital")} testid={`param-capital-${i}`} />
      <F label="Biaya operasional stabil/tahun (Rp)" value={d.opex} onChange={set("opex")} testid={`param-opex-${i}`} />
      <div />
      {SCEN.map((s) => <F key={s} label={`Produksi ${s.toLowerCase()} (kg/th)`} value={d.kg[s]} onChange={set("kg", s)} testid={`param-kg-${i}-${s}`} />)}
      <div />
      {SCEN.map((s) => <F key={s} label={`Estimasi awal BEP ${s.toLowerCase()} (th)`} value={d.initialBep?.[s] ?? ""} onChange={set("initialBep", s)} testid={`param-bep-${i}-${s}`} />)}
    </div>
  );
};

const ShareEditor = ({ label, value, onChange, testid }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={testid}>
    <p className="mb-2 text-sm font-semibold">{label} · total {PARTIES.reduce((a, p) => a + (Number(value[p]) || 0), 0)}%</p>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{PARTIES.map((p) => <F key={p} label={`${PARTY_LABEL[p]} (%)`} value={value[p]} onChange={(v) => onChange({ ...value, [p]: v })} testid={`${testid}-${p}`} />)}</div>
  </div>
);

const toForm = (p) => ({ ...p, productionPct: p.productionPct.join(", "), opexPct: p.opexPct.join(", "), terms: p.terms.join(", "), changeNote: "",
  pre: Object.fromEntries(PARTIES.map((k) => [k, p.pre[k] / 100])), post: Object.fromEntries(PARTIES.map((k) => [k, p.post[k] / 100])) });
const toBody = (f) => ({
  pricePerKg: Number(f.pricePerKg), productionPct: list(f.productionPct), opexPct: list(f.opexPct), terms: list(f.terms), changeNote: f.changeNote,
  densities: f.densities.map((d) => ({ trees: Number(d.trees), capital: Number(d.capital), opex: Number(d.opex),
    kg: Object.fromEntries(SCEN.map((s) => [s, Number(d.kg[s])])), initialBep: Object.fromEntries(SCEN.map((s) => [s, Number(d.initialBep?.[s]) || 0])) })),
  pre: Object.fromEntries(PARTIES.map((k) => [k, Math.round(Number(f.pre[k]) * 100)])), post: Object.fromEntries(PARTIES.map((k) => [k, Math.round(Number(f.post[k]) * 100)])),
});

export default function AdminSimParams() {
  const [f, setF] = useState(null);
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/public/asumsi-bagi-hasil").then((r) => setF(toForm(r.data.params)));
    api.get("/admin/asumsi-bagi-hasil/history").then((r) => setHist(r.data));
  };
  useEffect(() => { load(); }, []);
  if (!f) return <p className="text-sm text-slate-500">Memuat…</p>;
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/asumsi-bagi-hasil", toBody(f)); toast.success("Parameter simulasi diperbarui"); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="admin-sim-params-page">
      <div><h1 className="text-2xl font-bold text-slate-900">Parameter Asumsi Bagi Hasil</h1>
        <p className="text-sm text-slate-600">Versi {f.version}. Perubahan hanya memengaruhi halaman simulasi publik, tidak mengubah kontrak aktif atau transaksi. <Link to="/asumsi-bagi-hasil" className="text-emerald-800 underline" data-testid="admin-sim-open-public">Lihat halaman publik</Link></p></div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <F label="Harga jual (Rp/kg green bean)" value={f.pricePerKg} onChange={(v) => setF({ ...f, pricePerKg: v })} testid="param-price" />
        <F label="Produksi per tahun (% , th 1, 2, …; terakhir berlaku seterusnya)" value={f.productionPct} onChange={(v) => setF({ ...f, productionPct: v })} testid="param-production-pct" />
        <F label="Biaya operasional per tahun (%)" value={f.opexPct} onChange={(v) => setF({ ...f, opexPct: v })} testid="param-opex-pct" />
        <F label="Pilihan masa kontrak (tahun)" value={f.terms} onChange={(v) => setF({ ...f, terms: v })} testid="param-terms" />
      </div>
      {f.densities.map((d, i) => <DensityEditor key={i} i={i} d={d} onChange={(nd) => setF({ ...f, densities: f.densities.map((x, j) => (j === i ? nd : x)) })} />)}
      <ShareEditor label="Pembagian sebelum BEP" value={f.pre} onChange={(v) => setF({ ...f, pre: v })} testid="param-pre" />
      <ShareEditor label="Pembagian setelah BEP" value={f.post} onChange={(v) => setF({ ...f, post: v })} testid="param-post" />
      <F label="Alasan perubahan (wajib)" value={f.changeNote} onChange={(v) => setF({ ...f, changeNote: v })} testid="param-change-note" />
      <Button disabled={busy || f.changeNote.trim().length < 5} onClick={save} className="bg-emerald-800 hover:bg-emerald-900" data-testid="param-save-btn">Simpan Parameter</Button>
      {hist.length > 0 && <ul className="text-xs text-slate-600 space-y-1" data-testid="param-history">{hist.map((h) => <li key={`${h.version}-${h.archivedAt}`}>Versi {h.version} · diganti {h.archivedAt?.slice(0, 16).replace("T", " ")}{h.changeNote ? ` · ${h.changeNote}` : ""}</li>)}</ul>}
    </div>
  );
}
